import React, { useState, useContext } from "react";
import type { CognitoUser } from "amazon-cognito-identity-js";
import { useHistory } from "react-router-dom";
import "../Login/LoginPage.scss";
import { completeNewPassword } from "../AuthApi";
import { AuthContext, type AuthUser } from "../AuthContext";
import { Controller, useForm, useWatch } from "react-hook-form";
import PasswordPolicy from "../PasswordPolicy/PasswordPolicy";
import { useTranslation } from "react-i18next";
import PasswordInput from "../../Forms/Controls/PasswordInput/PasswordInput";
import { Info } from "../Login/LoginPage";
import { TextButton } from "@SeemplicityDev/shared";

interface ChangePasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { authState, populateAuthentication } = useContext(AuthContext);
  const {
    handleSubmit,
    setError,
    control,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>({
    mode: "onSubmit",
  });

  const passwordValue = useWatch({ control, name: "newPassword" });
  const confirmPasswordValue = useWatch({ control, name: "confirmPassword" });

  const { t } = useTranslation();
  let history = useHistory();

  const changePassword = (data: ChangePasswordFormValues) => {
    setIsSubmitting(true);
    // completeNewPassword deals in the raw aws-amplify CognitoUser; auth state holds
    // the augmented AuthUser (nullable, as the original JS passed it through), so
    // bridge across the cast at the call site.
    completeNewPassword(
      authState.user as unknown as CognitoUser,
      data.newPassword
    )
      .then((cognitoUser) => {
        const authUser = cognitoUser as unknown as AuthUser;
        return populateAuthentication(
          authUser,
          authUser?.preferredMFA as string | null | undefined
        );
      })
      .then(() => {
        reset();
        history.push("/dashboard");
      })
      .catch(() => {
        setError("confirmPassword", {
          message: "An error occurred. Change password failed",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="login-page-body">
      <h2 className="login-page-title">{t("Create Password")}</h2>
      <form className="login-page-form" onSubmit={handleSubmit(changePassword)}>
        <Controller
          // @ts-expect-error wrapperClassName is not a Controller prop; dead prop ignored at runtime, kept to preserve behavior
          wrapperClassName={`login-username`}
          name={"newPassword"}
          render={({ field: { onChange, onBlur, value } }) => {
            return (
              <PasswordInput
                dataTestId={"new-password"}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={"New Password"}
                autoComplete={"new-password"}
                isLarge
              />
            );
          }}
          rules={{
            required: true,
            validate: (value) => {
              if (value && confirmPasswordValue) {
                return (
                  confirmPasswordValue === value || "Passwords should match"
                );
              }
            },
          }}
          control={control}
        />

        <Controller
          // @ts-expect-error wrapperClassName is not a Controller prop; dead prop ignored at runtime, kept to preserve behavior
          wrapperClassName={`login-password`}
          name={"confirmPassword"}
          render={({ field: { onChange, onBlur, value } }) => {
            return (
              <PasswordInput
                dataTestId={"repeat-new-password"}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                isLarge
                autoComplete={"new-password"}
                placeholder={"Confirm New Password"}
              />
            );
          }}
          rules={{
            required: true,
            validate: (value) => {
              if (value && passwordValue) {
                return passwordValue === value || "Passwords should match";
              }
            },
          }}
          control={control}
        />

        {(errors.confirmPassword?.message || errors.newPassword?.message) && (
          <Info
            type={"error"}
            message={
              errors.confirmPassword?.message || errors.newPassword?.message
            }
          />
        )}

        <TextButton
          type="submit"
          label={!isSubmitting ? "Continue" : ""}
          rightIcon={isSubmitting ? "seem-icon-spinner spinner" : ""}
          buttonStyle={"primary"}
          disabled={isSubmitting}
          className="login-btn"
          isFullWidth
          size={"xl"}
          isSquare
          data-testid={"change-password"}
        />
      </form>

      {passwordValue && <PasswordPolicy password={passwordValue} />}
    </div>
  );
};

export default ChangePassword;
